import { useCallback, useMemo, useState } from "react";
import {
  ESC_KEY_HANDLING_PRIORITIES,
  useDisplayOrder,
  useGlobalOnEscapeKey,
} from "primereact/hooks";

// While an overlay (a dropdown list, a sheet) is open, register its close in
// PrimeReact's Escape queue at overlay priority, the way Calendar does: the
// queue runs only the most important handler, so Escape closes the overlay and
// not a Dialog (or Sidebar) around it. close should keep its identity.
export const useOverlayEscape = (open, close) => {
  const displayOrder = useDisplayOrder("overlay-panel", open);
  const priority = useMemo(
    () => [ESC_KEY_HANDLING_PRIORITIES.OVERLAY_PANEL, displayOrder],
    [displayOrder]
  );

  useGlobalOnEscapeKey({
    callback: close,
    when: open && displayOrder,
    priority,
  });
};

// PrimeReact's Dropdown and MultiSelect close their panel from their own key
// handler but, unlike Calendar, do not join PrimeReact's Escape queue, so a
// Dialog around them also closed on the same Escape (E2E8). While this field's
// panel is open, its close joins the queue at overlay priority (useOverlayEscape).
export const useOverlayPanelEscape = (componentRef, { onShow, onHide } = {}) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const closePanel = useCallback(() => componentRef.current?.hide(), [componentRef]);
  useOverlayEscape(panelOpen, closePanel);

  return {
    onShow: () => {
      setPanelOpen(true);
      onShow?.();
    },
    onHide: () => {
      setPanelOpen(false);
      onHide?.();
    },
  };
};
